import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  Kayttaja,
  KayttajaTapahtuma,
  TrivoreKayttaja,
  TrivoreOfficer,
  Tukipyynto,
} from "../types";
import {
  augmentoiTukipyynto,
  type TukipyyntoRaaka,
} from "./augmentoiTukipyynto";
// ─── DEMO: Tilin yhdistäminen (prototyyppidemo — poista testin jälkeen) ───
import { type MergeArgs } from "../demo/tilinYhdistaminenHelper";
// ─── /DEMO ───

/**
 * RTK Query -rajapinta NestJS-backendille (K6, 11.6.2026).
 * Yksi API-slice, useita endpointteja (RTK-idiomaattinen — sama baseUrl).
 *
 * - Tukipyynnöt: backend palauttaa raakana (12 perustietoa) → augmentoidaan
 *   frontendissä (kasittelyhistoria/laite/vastaus, sama logiikka kuin mock).
 * - Käyttäjät: backend palauttaa täydet kentät → muistiinpanoSticky johdetaan
 *   Context-storessa (SET_KAYTTAJAT → alustaMuistiinpanot).
 * - Virkailijat: backend palauttaa täydet kentät (GET /officers).
 *
 * baseUrl `/api` → Vite dev-proxy ohjaa `localhost:3000`:een (ei CORS-säätöä).
 * Vain LUKU tässä vaiheessa — toimenpiteet mutatoivat Context-storen
 * client-side -kopiota, eivät tallennu backendiin.
 */

const baseQuery = fetchBaseQuery({ baseUrl: "/api/" });

const baseQueryWithAuth = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions);

  // Check if response status is 401 (Unauthorized)
  if (
    result.error &&
    typeof result.error === "object" &&
    "status" in result.error &&
    result.error.status === 401
  ) {
    console.warn("Session expired (401), redirecting to login");
    window.location.href = "/api/login";
  }

  return result;
};

export const backendApi = createApi({
  reducerPath: "backendApi",
  // ─── DEMO: Tilin yhdistäminen — kääre injektoi demoentiteetit selaimessa,
  // ei kirjoita backendiin. Ilman demoa: fetchBaseQuery({ baseUrl: "/api/" }). ───
  baseQuery: baseQueryWithAuth,
  // ─── /DEMO ───
  tagTypes: ["Officer", "Ticket", "Kayttaja"],
  endpoints: (builder) => ({
    manualVerification: builder.mutation<
      void,
      {
        kayttajaId: string;
        etunimi: string;
        sukunimi: string;
        syntymaaika: string;
        asiakirjaTyyppi: string;
        maakoodi: string;
        asiakirjaNumero: string;
        vanhenee: string;
        kuvatLiitetty: boolean;
        sukupuoli: string;
        kansalaisuus: string;
        dokumenttiEtupuoli?: File;
        dokumenttiTakapuoli?: File;
        selfie?: File;
      }
    >({
      query: (data) => {
        const formData = new FormData();

        // Append all text fields
        formData.append("kayttajaId", data.kayttajaId);
        formData.append("etunimi", data.etunimi);
        formData.append("sukunimi", data.sukunimi);
        formData.append("syntymaaika", data.syntymaaika);
        formData.append("asiakirjaTyyppi", data.asiakirjaTyyppi);
        formData.append("maakoodi", data.maakoodi);
        formData.append("asiakirjaNumero", data.asiakirjaNumero);
        formData.append("vanhenee", data.vanhenee);
        formData.append("kuvatLiitetty", data.kuvatLiitetty.toString());
        formData.append("sukupuoli", data.sukupuoli);
        formData.append("kansalaisuus", data.kansalaisuus);

        // Append files
        if (data.dokumenttiEtupuoli) {
          formData.append("dokumenttiEtupuoli", data.dokumenttiEtupuoli);
        }
        if (data.dokumenttiTakapuoli) {
          formData.append("dokumenttiTakapuoli", data.dokumenttiTakapuoli);
        }
        if (data.selfie) {
          formData.append("selfie", data.selfie);
        }

        return {
          url: `users/${data.kayttajaId}/manual-verification`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Kayttaja", id: arg.kayttajaId },
        "Kayttaja",
      ],
    }),

    bypassIdProofing: builder.mutation<
      void,
      { uid: string; perustekentta: string }
    >({
      query: ({ uid, perustekentta }) => ({
        url: `users/${uid}/bypass-id-proofing`,
        method: "POST",
        body: { perustekentta },
      }),
      invalidatesTags: (result, error, { uid }) => [
        { type: "Kayttaja", id: uid },
        "Kayttaja",
      ],
    }),

    getTukipyynnot: builder.query<Tukipyynto[], void>({
      query: () => "tickets",
      transformResponse: (raw: TukipyyntoRaaka[]) =>
        raw.map(augmentoiTukipyynto),
      providesTags: ["Ticket"],
    }),

    mergeUserAccounts: builder.mutation<
      void,
      {
        sourceUid: string; // User being merged (the one with the ticket)
        targetUid: string; // Target user (the old account)
        reason: string;
      }
    >({
      query: ({ sourceUid, targetUid, reason }) => ({
        url: `users/${sourceUid}/merge`,
        method: "POST",
        body: { targetUid, reason },
      }),
      invalidatesTags: (result, error, { sourceUid, targetUid }) => [
        { type: "Kayttaja", id: sourceUid },
        { type: "Kayttaja", id: targetUid },
        "Kayttaja",
      ],
    }),

    getKayttajaByPersonalId: builder.query<
      Kayttaja,
      string // personalId (e.g., "562421866U")
    >({
      query: (personalId) =>
        `users/byPersonalId/${encodeURIComponent(personalId)}`,
      providesTags: (result, error, personalId) => [
        { type: "Kayttaja", id: personalId },
      ],
    }),

    getKayttajat: builder.query<Kayttaja[], { userIds: string[] } | void>({
      query: (params) => {
        if (!params || !params.userIds || params.userIds.length === 0) {
          return "users";
        }
        // POST request with userIds in body
        return {
          url: "users/batch",
          method: "POST",
          body: { userIds: params.userIds },
        };
      },
      providesTags: ["Kayttaja"],
    }),

    getOfficers: builder.query<TrivoreOfficer[], void>({
      query: () => "officers/officers",
      providesTags: ["Officer"],
    }),

    getKayttajaTiedosto: builder.query<Blob, { uid: string; filename: string }>(
      {
        query: ({ uid, filename }) =>
          `users/${uid}/tiedostot/${encodeURIComponent(filename)}`,
        transformResponse: async (response: Response) => {
          return await response.blob();
        },
      },
    ),

    updateUserGroups: builder.mutation<
      TrivoreKayttaja,
      { id: string; memberOf: string[] }
    >({
      query: ({ id, memberOf }) => ({
        url: `officers/${id}/groups`,
        method: "PUT",
        body: { memberOf },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Officer",
        { type: "Kayttaja", id },
      ],
    }),

    searchKayttajat: builder.query<Kayttaja[], string | null>({
      query: (q) => {
        if (!q || q.length < 2) return { url: "", skip: true };
        return `users/search?q=${encodeURIComponent(q)}`;
      },
      providesTags: ["Kayttaja"],
    }),

    getTukipyynto: builder.query<Tukipyynto, string>({
      query: (numeroOrId) => {
        // Try to detect if it's a tikettinumero (alphanumeric) or mongo id (hex)
        // Tikettinumero format: #1234 or similar
        if (/^[A-Za-z0-9#]+$/.test(numeroOrId) && numeroOrId.length < 30) {
          return `tickets/numero/${encodeURIComponent(numeroOrId)}`;
        }
        // Fall back to ID search for mongo IDs
        return `tickets/${numeroOrId}`;
      },
      transformResponse: (raw: TukipyyntoRaaka) => augmentoiTukipyynto(raw),
      providesTags: (result, error, id) => [{ type: "Ticket", id }],
    }),

    advancedSearchKayttajat: builder.mutation<
      Kayttaja[],
      {
        sukunimi?: string;
        etunimet?: string;
        syntymaaika?: string;
        sukupuoli?: string;
        kansalaisuus?: string;
        asiakirjanNumero?: string;
        myontajamaa?: string;
        myontamispaiva?: string;
        voimassaolopaiva?: string;
      }
    >({
      query: (filters) => ({
        url: "users/advanced-search",
        method: "POST",
        body: filters,
      }),
      invalidatesTags: ["Kayttaja"],
    }),

    testUserGet: builder.query<Kayttaja, void>({
      query: () => "users/testUserGet",
    }),

    deleteKayttaja: builder.mutation<void, { uid: string }>({
      query: ({ uid }) => ({
        url: `users/${uid}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { uid }) => [
        { type: "Kayttaja", id: uid },
        "Kayttaja",
      ],
    }),

    getKayttajaCustom: builder.query<Kayttaja, string>({
      query: (id) => `users/${id}/full`,
      providesTags: (result, error, id) => [{ type: "Kayttaja", id }],
    }),

    lockUser: builder.mutation<void, { uid: string; lockReason: string }>({
      query: ({ uid, lockReason }) => ({
        url: `users/${uid}/lock`,
        method: "POST",
        body: { lockReason },
      }),
      invalidatesTags: (r, e, { uid }) => [{ type: "Kayttaja", id: uid }],
    }),

    unlockUser: builder.mutation<void, { uid: string; logEventText: string }>({
      query: ({ uid, logEventText }) => ({
        url: `users/${uid}/lock`,
        method: "DELETE",
        body: { logEventText },
      }),
      invalidatesTags: (r, e, { uid }) => [{ type: "Kayttaja", id: uid }],
    }),

    // Fetch the currently authenticated user
    getCurrentUser: builder.query<TrivoreKayttaja, void>({
      query: () => "users/me",
      providesTags: ["Officer", "Kayttaja"],
    }),

    deleteOfficer: builder.mutation<void, string>({
      query: (id) => ({
        url: `officers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Officer"], // refetch list after deletion
    }),

    createOfficer: builder.mutation<
      TrivoreOfficer,
      {
        email: string;
        name: { givenName: string; familyName: string };
        memberOf: string[];
        password?: string; // ← optional
      }
    >({
      query: (body) => ({
        url: "officers",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Officer"],
    }),

    recoverOfficer: builder.mutation<void, string>({
      query: (id) => ({
        url: `officers/${id}/recover`,
        method: "POST",
      }),
      invalidatesTags: ["Officer"], // refetch so tila updates
    }),

    getTukipyynnotByKayttaja: builder.query<Tukipyynto[], string>({
      query: (kayttajaUid) => `tickets/kayttaja/${kayttajaUid}`,
      transformResponse: (raw: TukipyyntoRaaka[]) =>
        raw.map(augmentoiTukipyynto),
      providesTags: ["Ticket"],
    }),
    assignTicket: builder.mutation<Tukipyynto, string>({
      query: (id) => ({
        url: `tickets/numero/${encodeURIComponent(id)}/assign`,
        method: "PUT",
      }),
      transformResponse: (raw: TukipyyntoRaaka) => augmentoiTukipyynto(raw),
      invalidatesTags: (result, error, id) => [
        { type: "Ticket", id },
        "Ticket",
      ],
    }),

    unassignTicket: builder.mutation<Tukipyynto, string>({
      query: (id) => ({
        url: `tickets/numero/${encodeURIComponent(id)}/unassign`,
        method: "PUT",
      }),
      transformResponse: (raw: TukipyyntoRaaka) => augmentoiTukipyynto(raw),
      invalidatesTags: (result, error, id) => [
        { type: "Ticket", id },
        "Ticket",
      ],
    }),

    sendResolvedTicket: builder.mutation<
      void,
      {
        ticketId: string;
        userEmail: string;
        userName: string;
        response: string;
      }
    >({
      query: ({ ticketId, userEmail, userName, response }) => ({
        url: `tickets/${ticketId}/send-resolved`,
        method: "POST",
        body: { userEmail, userName, response },
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: "Ticket", id: ticketId },
        "Ticket",
      ],
    }),

    approveDocumentUpdate: builder.mutation<
      void,
      {
        uid: string;
        ticketId: string;
        tapahtuma: KayttajaTapahtuma;
        response: string;
      }
    >({
      query: ({ uid, ticketId, tapahtuma, response }) => ({
        url: `users/${uid}/approve-document-update`,
        method: "POST",
        body: { ticketId, tapahtuma, response },
      }),
      invalidatesTags: (result, error, { uid, ticketId }) => [
        { type: "Kayttaja", id: uid },
        { type: "Ticket", id: ticketId },
        "Ticket",
      ],
    }),

    denyDocumentUpdate: builder.mutation<
      void,
      {
        uid: string;
        ticketId: string;
        tapahtuma: KayttajaTapahtuma;
        response: string;
      }
    >({
      query: ({ uid, ticketId, tapahtuma, response }) => ({
        url: `users/${uid}/deny-document-update`,
        method: "POST",
        body: { ticketId, tapahtuma, response },
      }),
      invalidatesTags: (result, error, { uid, ticketId }) => [
        { type: "Kayttaja", id: uid },
        { type: "Ticket", id: ticketId },
        "Ticket",
      ],
    }),

    assignAllByKayttaja: builder.mutation<Tukipyynto[], string>({
      query: (kayttajaUid) => ({
        url: `tickets/kayttaja/${kayttajaUid}/assign-all`,
        method: "PUT",
      }),
      transformResponse: (raw: TukipyyntoRaaka[]) =>
        raw.map(augmentoiTukipyynto),
      invalidatesTags: ["Ticket"],
    }),
    resolveTicket: builder.mutation<
      void,
      {
        ticketId: string;
        response: string;
      }
    >({
      query: ({ ticketId, response }) => ({
        url: `tickets/${ticketId}/resolve`,
        method: "POST",
        body: { response },
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: "Ticket", id: ticketId },
        "Ticket",
      ],
    }),
  }),
});

export const {
  useManualVerificationMutation,
  useGetTukipyynnotQuery,
  useGetKayttajatQuery,
  useTestUserGetQuery,
  useGetCurrentUserQuery,
  useGetTukipyyntoQuery,
  useGetOfficersQuery,
  useGetKayttajaCustomQuery,
  useUpdateUserGroupsMutation,
  useDeleteOfficerMutation,
  useRecoverOfficerMutation,
  useCreateOfficerMutation,
  useGetTukipyynnotByKayttajaQuery,
  useAssignTicketMutation,
  useUnassignTicketMutation,
  useAssignAllByKayttajaMutation,
  useLockUserMutation,
  useUnlockUserMutation,
  useGetKayttajaTiedostoQuery,
  useSearchKayttajatQuery,
  useAdvancedSearchKayttajatMutation,
  useDeleteKayttajaMutation,
  useBypassIdProofingMutation,
  useGetKayttajaByPersonalIdQuery,
  useMergeUserAccountsMutation,
  useResolveTicketMutation,
  useSendResolvedTicketMutation,
  useApproveDocumentUpdateMutation,
  useDenyDocumentUpdateMutation,
} = backendApi;
